/**
 * A kebab button and the menu it opens.
 *
 * **Why it exists.** This lived inside `ProviderBlock`, which was the only
 * thing that needed it. #2194 needs three more — one for the whole test
 * list, one for the open test, one per suite — and four copies of a menu
 * is four places for the dismiss behaviour to drift. So it moved here and
 * `ProviderBlock` uses it like everyone else; the extraction is meant to
 * be behaviour-neutral, and anything that looks odd here looked odd there.
 *
 * **What it owns.** Open state, dismissal, and how an item renders.
 * Nothing about what the items DO — the caller passes those, because that
 * is the only part that differs between four callers.
 *
 * **Dismissal is the fiddly part.** A menu that closes on outside click but
 * not on Escape traps a keyboard user; one that closes on either but does
 * not clean up its listeners leaks one per open. Both are handled once
 * here rather than four times.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { MoreHorizontal, MoreVertical } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface KebabMenuItem {
  /** React key, and the handle a test uses to find the item. */
  key: string
  label: string
  /** Optional leading icon, sized by the menu rather than the caller. */
  icon?: ReactNode
  onClick: () => void
  disabled?: boolean
  /** Renders in the destructive colour. Pair with `separator` so a
   * destructive item is never adjacent to a safe one it could be
   * mistaken for. */
  danger?: boolean
  /** Draws a divider ABOVE this item. */
  separator?: boolean
}

export interface KebabMenuProps {
  items: KebabMenuItem[]
  /** What the button announces to a screen reader. Say what it acts on —
   * "More actions" three times on one screen tells nobody which is which. */
  ariaLabel: string
  /** Vertical dots by default; horizontal where the row is tight. */
  orientation?: 'vertical' | 'horizontal'
  /** Extra classes for the trigger. */
  className?: string
  /** Narrower menus suit one-word actions; the default fits a sentence. */
  width?: 'default' | 'narrow'
}

export function KebabMenu({
  items,
  ariaLabel,
  orientation = 'vertical',
  className,
  width = 'default',
}: KebabMenuProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  // Close on a click outside the container or on Escape. Escape matters
  // for a keyboard user, who otherwise has no way out but to activate
  // something. Listeners are attached only while open and removed on
  // close, so repeated opening does not accumulate them.
  useEffect(() => {
    if (!open) return
    function handleOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  if (items.length === 0) return null
  const Dots = orientation === 'vertical' ? MoreVertical : MoreHorizontal

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen(v => !v)}
        className={cn(
          'w-7 h-7 flex items-center justify-center rounded border text-muted-foreground transition-colors',
          open ? 'bg-background border-border' : 'border-transparent hover:bg-background hover:border-border',
          className,
        )}
      >
        <Dots className="h-4 w-4" />
      </button>

      {open && (
        <div
          role="menu"
          className={cn(
            'absolute right-0 top-[calc(100%+4px)] z-10 rounded-md border border-border bg-background shadow-md py-1',
            width === 'narrow' ? 'min-w-[160px]' : 'min-w-[220px]',
          )}
        >
          {items.map(item => (
            <div key={item.key}>
              {item.separator && <div className="my-1 h-px bg-border" role="separator" />}
              <button
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  // Close first: the handler may open a dialog, and a menu
                  // still on screen behind it reads as two things at once.
                  setOpen(false)
                  item.onClick()
                }}
                className={cn(
                  'w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  item.danger ? 'text-destructive hover:bg-destructive/10' : 'text-foreground hover:bg-muted',
                )}
              >
                {item.icon && (
                  <span className={cn(
                    'w-4 h-4 flex items-center justify-center flex-shrink-0',
                    item.danger ? 'text-destructive' : 'text-muted-foreground',
                  )}>
                    {item.icon}
                  </span>
                )}
                <span>{item.label}</span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
